if (!customElements.get('sticky-atc-bar')) {
  customElements.define(
    'sticky-atc-bar',
    class StickyATCBar extends HTMLElement {
      constructor() {
        super();
        this.isVisible = false;
        this.variantChangeUnsubscriber = undefined;
        this.cartUpdateUnsubscriber = undefined;
        this.handleDomVariantChange = this.handleDomVariantChange?.bind
          ? this.handleDomVariantChange.bind(this)
          : (event) => this.handleDomVariantChange(event);
        this.productForm = undefined;
        this.variantInput = undefined;
        this.submitButton = undefined;
        this.variantText = undefined;
        this.imageContainer = undefined;
        this.sectionId = this.dataset.sectionId;
        this.productInfoElement = null;
        this.intersectionObserver = null;
      }

      connectedCallback() {
        this.initializeElements();
        this.setupScrollListener();
        this.setupVariantChangeListener();
        this.setupFormSubmission();
        this.initializeVariantInfo();

        // Also listen for custom DOM "variant:change" events (used by custom pickers)
        document.addEventListener('variant:change', this.handleDomVariantChange);
      }

      initializeVariantInfo() {
        // Get initial variant info from the page
        const mainProductForm = document.querySelector(`#product-form-${this.sectionId}`);
        if (mainProductForm) {
          const mainVariantInput = mainProductForm.querySelector('input[name="id"]');
          if (mainVariantInput && mainVariantInput.value) {
            // Image is already set in the initial HTML, no need to update here
            // It will be updated when variant changes
          }
        }
      }

      initializeElements() {
        this.productForm = this.querySelector('product-form');
        this.variantInput = this.querySelector('.sticky-product-variant-id');
        this.submitButton = this.querySelector('.sticky-atc-bar__button');
        this.variantText = this.querySelector(`#StickyATC-Variant-${this.sectionId}`);
        this.imageContainer = this.querySelector(`#StickyATC-Image-${this.sectionId}`);
        this.priceElement = this.querySelector(`#StickyATC-Price-${this.sectionId}`);
      }

      setupScrollListener() {
        // Function to find and observe product-info element
        const findAndObserveProductInfo = () => {
          const productId = this.dataset.productId;
          
          if (productId) {
            // Try to find product-info by product ID
            this.productInfoElement = document.querySelector(`product-info[data-product-id="${productId}"]`);
          }
          
          // If not found by ID, try to find any product-info element
          if (!this.productInfoElement) {
            this.productInfoElement = document.querySelector('product-info');
          }

          if (this.productInfoElement) {
            // Use Intersection Observer to detect when product-info is in/out of viewport
            this.intersectionObserver = new IntersectionObserver(
              (entries) => {
                entries.forEach((entry) => {
                  // Hide bar when product-info is visible in viewport
                  if (entry.isIntersecting) {
                    this.isVisible = false;
                    this.classList.remove('is-visible');
                  } else {
                    // Show bar when product-info is out of viewport
                    this.isVisible = true;
                    this.classList.add('is-visible');
                  }
                });
              },
              {
                root: null,
                rootMargin: '0px',
                threshold: 0
              }
            );

            this.intersectionObserver.observe(this.productInfoElement);
            
            // Check initial state so bar matches scroll position on load
            const checkInitialState = () => {
              const rect = this.productInfoElement.getBoundingClientRect();
              const isInViewport = rect.top < window.innerHeight && rect.bottom > 0;

              if (isInViewport) {
                this.isVisible = false;
                this.classList.remove('is-visible');
              } else {
                this.isVisible = true;
                this.classList.add('is-visible');
              }
            };
            
            // Check immediately
            checkInitialState();
            
            // Also check after a short delay to handle dynamic content
            setTimeout(checkInitialState, 100);
            
            return true; // Successfully found and observing
          }
          
          return false; // Not found
        };

        // Try to find product-info immediately
        if (!findAndObserveProductInfo()) {
          // If not found, wait for DOM to be ready and try again
          if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => {
              if (!findAndObserveProductInfo()) {
                // Still not found after DOM ready, show immediately
                this.isVisible = true;
                this.classList.add('is-visible');
              }
            });
          } else {
            // DOM already ready, try one more time after a short delay
            setTimeout(() => {
              if (!findAndObserveProductInfo()) {
                // Not found, show immediately
                this.isVisible = true;
                this.classList.add('is-visible');
              }
            }, 500);
          }
        }
      }

      setupVariantChangeListener() {
        if (typeof subscribe === 'undefined' || typeof PUB_SUB_EVENTS === 'undefined') {
          return;
        }

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          // Update whenever variant changes on the product page
          // Since we're a separate section, we listen to all variant changes
          const productId = this.dataset.productId;
          if (productId) {
            // Check if the variant change is for our product
            const productInfo = document.querySelector(`product-info[data-product-id="${productId}"]`);
            if (productInfo) {
              this.updateVariantInfo(event.data.variant, event.data.html);
            }
          } else {
            // If no product ID, update for any variant change (assumes single product page)
            this.updateVariantInfo(event.data.variant, event.data.html);
          }
        });
      }

      // Handle custom DOM variant change events (e.g. from custom flavor picker)
      handleDomVariantChange(event) {
        if (!event || !event.detail) return;
        const variant = event.detail.variant;
        if (!variant) return;

        // For DOM events we don't have the updated HTML fragment, so pass null
        this.updateVariantInfo(variant, null);
      }

      updateVariantInfo(variant, html) {
        if (!variant) {
          this.setUnavailable();
          return;
        }

        // Update variant ID in form
        if (this.variantInput) {
          this.variantInput.value = variant.id;
        }

        // Update variant text - build from variant options
        if (this.variantText) {
          let variantTitle = '';
          
          // Build variant title from options
          if (variant.options && variant.options.length > 0) {
            // Filter out empty options and join with " / "
            const optionValues = variant.options.filter(opt => opt && opt.trim() !== '');
            if (optionValues.length > 0) {
              variantTitle = optionValues.join(' / ');
            }
          }
          
          // Fallback to variant.title if options don't work
          if (!variantTitle && variant.title && variant.title !== 'Default Title') {
            variantTitle = variant.title;
          }
          
          // Only show if we have a valid variant title
          if (variantTitle && variantTitle.trim() !== '' && variantTitle !== 'Default Title') {
            this.variantText.textContent = variantTitle;
            this.variantText.style.display = '';
          } else {
            this.variantText.style.display = 'none';
          }
        }

        // Update price
        if (this.priceElement && variant.price != null) {
          const formattedPrice = Shopify.formatMoney ? Shopify.formatMoney(variant.price) : '$' + (variant.price / 100).toFixed(2);
          this.priceElement.innerHTML = formattedPrice;
        }

        // Update variant image
        if (this.imageContainer) {
          let imageUrl = null;
          let imageAlt = '';
          let imageId = null;

          // Try to get image from the updated HTML first (from product media gallery)
          if (html) {
            const mediaGallery = html.querySelector('.product__media-item img, .product__media img');
            if (mediaGallery) {
              imageUrl = mediaGallery.src;
              imageAlt = mediaGallery.alt || '';
              const mediaId = mediaGallery.getAttribute('data-media-id');
              if (mediaId) imageId = mediaId;
            }
          }

          // Fallback: Get from variant data
          if (!imageUrl && variant.featured_media) {
            // Try to construct image URL from variant featured media
            if (variant.featured_media.preview_image) {
              imageUrl = variant.featured_media.preview_image.src || 
                         (variant.featured_media.preview_image.url ? 
                          `https:${variant.featured_media.preview_image.url}` : null);
            }
            imageAlt = variant.featured_media.alt || variant.title || '';
            imageId = variant.featured_media.id;
          }

          // Final fallback: Get from current page product media
          if (!imageUrl) {
            const productInfo = document.querySelector(`product-info[data-product-id]`);
            if (productInfo) {
              const productMedia = productInfo.querySelector('.product__media-item img, .product__media img');
              if (productMedia) {
                imageUrl = productMedia.src;
                imageAlt = productMedia.alt || '';
              }
            }
          }

          // Update the image if we have a URL
          if (imageUrl) {
            const existingImg = this.imageContainer.querySelector('.sticky-atc-bar__img');
            if (existingImg) {
              // Update existing image with fade effect
              existingImg.style.opacity = '0.7';
              setTimeout(() => {
                existingImg.src = imageUrl;
                existingImg.srcset = `${imageUrl} 1x, ${imageUrl} 2x`;
                if (imageAlt) existingImg.alt = imageAlt;
                if (imageId) existingImg.setAttribute('data-media-id', imageId);
                existingImg.style.opacity = '1';
              }, 100);
            } else {
              // Create new image element
              const img = document.createElement('img');
              img.className = 'sticky-atc-bar__img';
              img.src = imageUrl;
              img.srcset = `${imageUrl} 1x, ${imageUrl} 2x`;
              img.alt = imageAlt || '';
              img.loading = 'lazy';
              if (imageId) img.setAttribute('data-media-id', imageId);
              this.imageContainer.innerHTML = '';
              this.imageContainer.appendChild(img);
            }
          }
        }

        // Update button state
        if (this.submitButton) {
          const isAvailable = variant.available !== false;
          this.submitButton.disabled = !isAvailable;
          
          const buttonText = this.submitButton.querySelector('.sticky-atc-bar__button-text');
          if (buttonText) {
            if (!isAvailable) {
              buttonText.textContent = window.variantStrings?.soldOut || 'Sold out';
            } else {
              buttonText.textContent = window.variantStrings?.addToCart || 'Add to cart';
            }
          }
        }
      }

      setUnavailable() {
        if (this.submitButton) {
          this.submitButton.disabled = true;
          const buttonText = this.submitButton.querySelector('.sticky-atc-bar__button-text');
          if (buttonText) {
            buttonText.textContent = window.variantStrings?.unavailable || 'Unavailable';
          }
        }
      }

      setupFormSubmission() {
        if (!this.productForm) return;

        const form = this.productForm.querySelector('form');
        if (!form) return;

        form.addEventListener('submit', (event) => {
          event.preventDefault();
          
          // Find the main product form - try multiple selectors
          // First try to find by section ID pattern
          let mainProductForm = document.querySelector(`#product-form-${this.sectionId}`);
          
          // If not found, try to find any product form on the page (from main product section)
          if (!mainProductForm) {
            const productInfo = document.querySelector('product-info');
            if (productInfo) {
              // Try to find form with data-type="add-to-cart-form"
              mainProductForm = productInfo.querySelector('form[data-type="add-to-cart-form"]');
              
              // If still not found, try to find any form with product-form class
              if (!mainProductForm) {
                const productFormElement = productInfo.querySelector('product-form');
                if (productFormElement) {
                  mainProductForm = productFormElement.querySelector('form');
                }
              }
            }
          }
          
          if (mainProductForm) {
            // Update variant ID in main form
            const mainVariantInput = mainProductForm.querySelector('input[name="id"]');
            if (mainVariantInput && this.variantInput) {
              mainVariantInput.value = this.variantInput.value;
            }
            
            // Sync quantity from main form to sticky form
            const mainQuantityInput = mainProductForm.querySelector('input[name="quantity"]');
            const stickyQuantityInput = form.querySelector('.sticky-product-quantity');
            if (mainQuantityInput && stickyQuantityInput) {
              stickyQuantityInput.value = mainQuantityInput.value;
            }
            
            // Trigger click on main form submit button to use existing form handling
            const mainSubmitButton = mainProductForm.querySelector('button[type="submit"]');
            if (mainSubmitButton && !mainSubmitButton.disabled) {
              mainSubmitButton.click();
            } else {
              // Fallback: submit the main form directly
              mainProductForm.requestSubmit();
            }
          } else {
            // Fallback: submit this form directly if no main form found
            this.productForm.submit();
          }
        });
      }

      disconnectedCallback() {
        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
        if (this.intersectionObserver) {
          this.intersectionObserver.disconnect();
        }
        document.removeEventListener('variant:change', this.handleDomVariantChange);
      }
    }
  );
}

